'use client';

import { Button, LoadingDark, Padding, Typography } from '@musinsa/mds';
import { useDialog } from '@musinsa/mds-ui';
import type { UpdateProfileImageServiceRequest } from '@musinsa/services-curator';
import { updateProfileImageService } from '@musinsa/services-curator';
import { useMutation, useQuery } from '@tanstack/react-query';
import type React from 'react';
import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useCuratorBroadcastPublisher } from '../../common';
import { useCuratorSnackbar } from '../../common/hooks';
import { useGoProfileEditNickname } from '../../common/paths/actions';
import { curatorQueries } from '../../query-factory';

interface ApiLikeError extends Error {
  data?: { meta?: { message?: string } };
  status?: number;
}

const NETWORK_FAILED_UPDATE_MESSAGE =
  '프로필 이미지 변경에 실패하였습니다. 문제가 계속된다면 PC에서 시도해주세요.';
const UPDATE_FAILED_MESSAGE = '프로필 이미지 변경에 실패했습니다. 잠시 후 다시 시도해주세요.';

interface Props {
  className?: string;
}

export const ProfileEditButtons = ({ className }: Props) => {
  const goNicknameChange = useGoProfileEditNickname();

  const { triggerQuerySync } = useCuratorBroadcastPublisher();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const snackbar = useCuratorSnackbar();
  const dialog = useDialog();

  const { data: userInfo } = useQuery({
    ...curatorQueries.profile.userInfo,
  });
  const snsTitle = userInfo?.snsTitle ?? '';

  const handleNicknameChange = () => {
    goNicknameChange();
  };

  const { mutate: updateProfileImage, isPending } = useMutation({
    mutationFn: (data: UpdateProfileImageServiceRequest) => updateProfileImageService(data),
    onSuccess: () => {
      if (snsTitle) {
        triggerQuerySync({
          queryKeys: [curatorQueries.profile.profileBySnsTitle(snsTitle).queryKey],
        });
      }
      snackbar.success('프로필 이미지가 변경되었습니다.');
    },
    onError: (error: Error) => {
      const apiError = error as ApiLikeError;

      const apiMessage = apiError.data?.meta?.message;
      if (apiMessage) {
        snackbar.error(apiMessage);
        return;
      }

      // HTTP 응답 없음 = 네트워크/업로드 단계 실패 (Android WebView content:// URI 만료 포함).
      // PC 환경으로의 복구 경로를 안내한다.
      if (apiError.status === undefined) {
        snackbar.error(NETWORK_FAILED_UPDATE_MESSAGE);
        return;
      }

      snackbar.error(UPDATE_FAILED_MESSAGE);
    },
  });

  const handleProfileImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      dialog.open({
        title: '파일을 선택해주세요.',
        confirmButtonText: '확인',
      });
      return;
    }

    const allowedTypes = ['image/gif', 'image/jpg', 'image/jpeg', 'image/png', 'image/heic'];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (!allowedTypes.includes(file.type)) {
      dialog.open({
        title: '지원하지 않는 파일 형식입니다.',
        message: 'GIF, JPG, PNG, HEIC 파일만 업로드 가능합니다.',
        confirmButtonText: '확인',
      });
      return;
    }

    if (file.size > maxSize) {
      dialog.open({
        title: '파일 크기가 너무 큽니다.',
        message: '2MB 이하의 파일만 업로드 가능합니다.',
        confirmButtonText: '확인',
      });
      return;
    }

    updateProfileImage({ profileImage: file });
  };

  return (
    <div className={twMerge('flex gap-[4px] pt-[8px] px-[16px] pb-[12px]', className)}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/gif,image/jpeg,image/jpg,image/png,image/heic"
        onChange={handleFileChange}
        className="hidden"
      />
      <Button
        size={36}
        variant="tertiaryOutlinedGray"
        onClick={handleProfileImageButtonClick}
        disabled={isPending}
        className="flex items-center flex-1"
      >
        <Padding className="flex items-center gap-2">
          {isPending ? <LoadingDark size="size12x12" /> : null}
          <Typography size="13" variant="body13pxMed">
            프로필 이미지 변경
          </Typography>
        </Padding>
      </Button>
      <Button
        size="36"
        variant="tertiaryOutlinedGray"
        onClick={handleNicknameChange}
        className="flex items-center flex-1"
      >
        <Typography size="13" variant="body13pxMed">
          닉네임 변경
        </Typography>
      </Button>
    </div>
  );
};
